import { useState } from "react";
import { Box, CircularProgress, Button, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LoopOutlinedIcon from "@mui/icons-material/LoopOutlined";
import useTranslationSetup from "../../utils/useTranslationSetup";
import CustomSwipableDrawer from "../customSwipableDrawer/CustomSwipableDrawer";
import AudioPlayer from "../audioPlayer/AudioPlayer";
import QualityChecksInstruction from "../QualityChecksInstruction/QualityChecksInstruction";
import DeleteAudioDialog from "../DeleteAudioDialog/DeleteAudioDialog";
import { getQualityFailImage } from "../../utils/audioQuality/getQualityFailImage";
import { getQualityIssueKey } from "../../utils/audioQuality/getQualityIssueKey";
import { produceEventLog, getUserProperties } from "../../utils/helper";
import useParamValues from "../../utils/useParamValues";
import "./styles.css";

function SaveAudioConfirmationDrawer({
  open,
  onOpenChange,
  audioBlob,
  audioBlobRef,
  setAudioBlobRef,
  audioQualityResult = null,
  onRetake,
  onSave,
  currentStep,
  audioUploading,
}) {
  const { t, activeLanguage } = useTranslationSetup();
  const userProperties = getUserProperties(useParamValues());

  const [openRetakeDialog, setOpenRetakeDialog] = useState(false);

  const qualityPassed = audioQualityResult?.passed === true;
  const qualityFailed = audioQualityResult?.passed === false;
  const qualityIssueKey = audioQualityResult
    ? getQualityIssueKey(audioQualityResult)
    : null;

  const pausePlayback = () => {
    audioBlobRef?.current?.pause();
  };

  const handleSubmitClick = () => {
    pausePlayback();
    onSave();
  };

  const handleRetakeClick = () => {
    pausePlayback();
    onOpenChange(false);
    setOpenRetakeDialog(true);
    produceEventLog("assessment_retake2_button", {
      ...userProperties,
      assessment_flow: currentStep,
    });
  };

  const handleRetakeConfirm = () => {
    setOpenRetakeDialog(false);
    onRetake();
  };

  return (
    <>
      <CustomSwipableDrawer
        open={open}
        preventDefault
        anchor="bottom"
        className="save-audio-confirmation"
      >
        {qualityIssueKey ? (
          <Box className="save-audio-quality-fail-banner">
            <img
              src={getQualityFailImage(qualityIssueKey)}
              alt=""
              className="save-audio-quality-fail-banner-img"
            />
          </Box>
        ) : null}

        <Typography className="save-audio-listen-heading" lang={activeLanguage}>
          {t("listen_to_recording")}
        </Typography>

        {audioBlob ? (
          <Box className="save-audio-confirmation-audio-player-container">
            <AudioPlayer
              audioBlob={audioBlob}
              audioBlobRef={audioBlobRef}
              setAudioBlobRef={setAudioBlobRef}
              currentStep={currentStep}
              onPlayEventName="assessment_play1_button"
              onPauseEventName="assessment_pause1_button"
            />
          </Box>
        ) : null}

        {qualityPassed ? (
          <Box className="save-audio-quality-instruction-container">
            <QualityChecksInstruction
              headingText={t("quality_instruction_heading")}
              borderColor="#F3F3F3"
              headingColor="#313B1A"
              contentColor="#6E6E6E"
              border="0.5px"
              boxShadow="0px 4px 34px 0px rgba(18, 18, 18, 0.06)"
              t={t}
            />
          </Box>
        ) : null}

        {qualityFailed ? (
          <Typography
            className="save-audio-quality-fail-prompt"
            lang={activeLanguage}
          >
            {t("submit_anyway_prompt")}
          </Typography>
        ) : null}

        <Box className="save-audio-confirmation-actions">
          <Button
            className="save-audio-action-button save-audio-submit-button btn-primary"
            variant="contained"
            lang={activeLanguage}
            disableElevation
            disabled={!audioBlob || audioUploading}
            onClick={handleSubmitClick}
            startIcon={!audioUploading && <CheckIcon sx={{ fontSize: 18 }} />}
          >
            {audioUploading ? (
              <Box className="loading-dialog-spinner-wrap">
                <CircularProgress
                  size={30}
                  thickness={4.5}
                  sx={{
                    color: "#FFF",
                  }}
                />
              </Box>
            ) : qualityFailed ? (
              t("submit_anyway")
            ) : (
              t("submit")
            )}
          </Button>

          <Button
            className="save-audio-action-button save-audio-retake-outline theme-color"
            variant="outlined"
            lang={activeLanguage}
            disableElevation
            disabled={audioUploading}
            onClick={handleRetakeClick}
            startIcon={
              <LoopOutlinedIcon
                sx={{
                  fontSize: 18,
                  transform: "rotateY(180deg) rotate(132deg)",
                }}
              />
            }
          >
            {t("retake")}
          </Button>
        </Box>
      </CustomSwipableDrawer>

      <DeleteAudioDialog
        isDialogOpen={openRetakeDialog}
        setDialogOpen={setOpenRetakeDialog}
        onConfirmRetake={handleRetakeConfirm}
      />
    </>
  );
}

export default SaveAudioConfirmationDrawer;
